// Styled listbox replacing the native <select>. The game shipped 15 unstyled OS dropdowns whose
// popup chrome is drawn by the platform and cannot be themed — the loudest "web form, not a game"
// tell in the UI.
//
// The popover is position: fixed off a measured trigger rect. The app is full-screen and several
// triggers sit inside scroll containers (deck filters, settings panels), so an absolutely
// positioned popover would clip; fixed + measurement avoids that without a positioning library.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** optional secondary line shown under the label in the popover */
  hint?: string;
  /** optional leading glyph or icon element */
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SelectProps<T extends string> {
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  /** rendered when `value` matches no option (e.g. the "Saved decks…" placeholder) */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const POPOVER_MARGIN = 6;
const MAX_POPOVER_HEIGHT = 320;

export function Select<T extends string>({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled,
  className,
  ariaLabel,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState<{ left: number; top: number; width: number; drop: 'down' | 'up' } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const typeAhead = useRef({ buffer: '', at: 0 });
  const listId = useId();

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const box = trigger.getBoundingClientRect();
    // Flip above the trigger when there isn't room below — settings/deck rows near the
    // bottom of the viewport would otherwise open off-screen.
    const below = window.innerHeight - box.bottom;
    const drop: 'down' | 'up' = below < Math.min(MAX_POPOVER_HEIGHT, options.length * 34 + 12) && box.top > below
      ? 'up'
      : 'down';
    setRect({
      left: box.left,
      top: drop === 'down' ? box.bottom + POPOVER_MARGIN : box.top - POPOVER_MARGIN,
      width: box.width,
      drop,
    });
  }, [options.length]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => measure();
    // capture:true so scrolling any ancestor container repositions the popover too
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, measure]);

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, selectedIndex]);

  const commit = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const step = (from: number, delta: number): number => {
    if (options.length === 0) return from;
    let next = from;
    for (let i = 0; i < options.length; i++) {
      next = (next + delta + options.length) % options.length;
      if (!options[next].disabled) return next;
    }
    return from;
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => step(index, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => step(index, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(step(options.length - 1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(step(0, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default: {
        if (event.key.length !== 1) break;
        // Type-ahead, matching native <select>: keystrokes within 700 ms accumulate.
        const now = event.timeStamp;
        const state = typeAhead.current;
        state.buffer = now - state.at > 700 ? event.key : state.buffer + event.key;
        state.at = now;
        const needle = state.buffer.toLowerCase();
        const match = options.findIndex((option) => !option.disabled && option.label.toLowerCase().startsWith(needle));
        if (match >= 0) setActiveIndex(match);
        break;
      }
    }
  };

  // Keep the active option in view during keyboard navigation.
  useEffect(() => {
    if (!open) return;
    popoverRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`select-trigger${open ? ' open' : ''}${className ? ` ${className}` : ''}`}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        data-audio="ui"
      >
        <span className="select-value">
          {selected?.icon}
          {selected ? selected.label : <em>{placeholder}</em>}
        </span>
        <span className="select-caret" aria-hidden="true" />
      </button>
      {open && rect && (
        <div
          ref={popoverRef}
          id={listId}
          className="select-popover"
          role="listbox"
          aria-label={ariaLabel}
          aria-activedescendant={`${listId}-${activeIndex}`}
          style={{
            left: rect.left,
            width: rect.width,
            ...(rect.drop === 'down'
              ? { top: rect.top, maxHeight: Math.min(MAX_POPOVER_HEIGHT, window.innerHeight - rect.top - 8) }
              : { bottom: window.innerHeight - rect.top, maxHeight: Math.min(MAX_POPOVER_HEIGHT, rect.top - 8) }),
          }}
          onKeyDown={onKeyDown}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled}
              data-active={index === activeIndex}
              className={`select-option${option.value === value ? ' selected' : ''}${option.disabled ? ' disabled' : ''}`}
              onPointerEnter={() => setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              {option.icon}
              <span className="select-option-label">{option.label}</span>
              {option.hint && <small>{option.hint}</small>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
